import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  FileText, 
  Users, 
  ClipboardList, 
  Gavel, 
  CheckSquare, 
  Shield,
  FileDown,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Pencil,
  Eye
} from "lucide-react";
import { DocumentPreviewModal } from "@/components/documents/DocumentPreviewModal";
import { FinalizeWithSignatures } from "@/components/documents/FinalizeWithSignatures";
import { DocumentSigningControl } from "@/components/workflows/DocumentSigningControl";
import { SealApplyCheckbox } from "@/components/workflows/SealApplyCheckbox";
import { useAssociationSeal } from "@/components/workflows/useAssociationSeal";
import { generatedFilePath } from "@/components/workflows/docProvenance";
import { finishLegacyHtml } from "@/components/workflows/legacyDocumentFinish";
import { legacySigningMeta } from "@/components/workflows/legacySigningSpecs";
import { embedSignaturesIntoHtml } from "@/lib/signatureEmbedding";
import { formatDateString } from "@/lib/utils";
import { 
  ReviewSummaryTab, 
  ReviewAttendeesTab, 
  ReviewAgendaTab, 
  ReviewMotionsTab, 
  ReviewActionsTab, 
  ReviewResolutionsTab,
  ReviewTranscriptTab 
} from "./review-tabs";

// Import editable section components for edit mode
import { AgendaSection } from "./sections/AgendaSection";
import { MotionsSection } from "./sections/MotionsSection";
import { ActionItemsSection } from "./sections/ActionItemsSection";
import { AttendeesSection } from "./sections/AttendeesSection";
import { NotesSection } from "./sections/NotesSection";
import { ResolutionsSection } from "./sections/ResolutionsSection";

interface MeetingMinutesReviewProps {
  meetingId: string;
  unaId: string;
  taskId: string;
  onPhaseComplete?: () => void;
}

interface TreasurySecretaryData {
  user_id: string;      // profiles.id / auth.users.id - for generated_documents.finalized_by
  member_id: string;    // una_members.id - for meeting_minutes.certified_by
  full_name: string;
}

// Helper to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24: string | null | undefined): string => {
  if (!time24) return '';
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time24;
  }
};

export function MeetingMinutesReview({
  meetingId,
  unaId,
  taskId,
  onPhaseComplete
}: MeetingMinutesReviewProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [meetingData, setMeetingData] = useState<any>(null);
  const [generatedDocument, setGeneratedDocument] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [treasurySecretaryData, setTreasurySecretaryData] = useState<TreasurySecretaryData | null>(null);
  const [unaData, setUnaData] = useState<any>(null);
  const seal = useAssociationSeal(unaId);
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    loadMeetingData();
    loadTreasurySecretaryData();
    loadUnaData();
  }, [meetingId, unaId]);

  const loadUnaData = async () => {
    if (!unaId) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          member_role,
          status
        )
      `)
      .eq('id', unaId)
      .maybeSingle();

    if (!error && data) {
      setUnaData(data);
    }
  };

  const loadTreasurySecretaryData = async () => {
    try {
      // Get UNA to find treasury_secretary_id
      const { data: una, error: unaError } = await supabase
        .from('unas')
        .select('treasury_secretary_id')
        .eq('id', unaId)
        .single();
        
      if (unaError || !una?.treasury_secretary_id) {
        console.warn('Could not find treasury secretary for UNA');
        return;
      }

      // Get the profile/name
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('id', una.treasury_secretary_id)
        .single();
        
      if (profileError) {
        console.warn('Could not find profile for treasury secretary');
        return;
      }

      // Get the una_members record for this user (needed for certified_by FK)
      const { data: member, error: memberError } = await supabase
        .from('una_members')
        .select('id')
        .eq('una_id', unaId)
        .eq('user_id', una.treasury_secretary_id)
        .eq('status', 'active')
        .maybeSingle();
      
      if (memberError) {
        console.warn('Could not find una_members record for treasury secretary:', memberError);
      }

      setTreasurySecretaryData({
        user_id: una.treasury_secretary_id,
        member_id: member?.id || '',  // una_members.id for certified_by FK
        full_name: profile?.full_name || 'Secretary Treasurer'
      });
    } catch (error) {
      console.error('Error loading treasury secretary data:', error);
    }
  };

  // Generate Meeting Minutes HTML dynamically (matching ResolutionPhase1 pattern)
  const generateMeetingMinutesHtml = (): string => {
    const formatDate = (dateStr: string) => {
      if (!dateStr) return formatDateString(new Date().toISOString().split('T')[0], 'MMMM d, yyyy');
      return formatDateString(dateStr, 'MMMM d, yyyy');
    };

    const esc = (v: any) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c]);
    const fallback = (v: any) => (v && String(v).trim().length > 0 ? String(v) : '[on file]');

    const unaName = unaData?.name || meetingData?.una?.name || '[UNA Name]';
    const meetingDate = formatDate(meetingData?.meeting_date);
    const meetingType = meetingData?.meeting_type || 'Officer Meeting';
    const meetingLocation = meetingData?.meeting_location || '';
    const meetingStartTime = meetingData?.meeting_start_time || '';
    const meetingEndTime = meetingData?.meeting_end_time || '';

    const primaryRole = unaData?.primary_officer_role || 'Secretary Treasurer';
    const treasurySecretaryName = treasurySecretaryData?.full_name || primaryRole;
    const treasurySecretaryUserId = treasurySecretaryData?.user_id || 'treasury_secretary';
    const recordingSecretaryName = meetingData?.recording_secretary?.full_name || treasurySecretaryName;

    // Association header line (canon form, mirrors resolution documents).
    const filingNo = (unaData as any)?.ca_filing_number || (unaData as any)?.filing_number || null;
    const ein = (unaData as any)?.ein || null;
    const principalAddress = (unaData as any)?.principal_address || (unaData as any)?.mailing_address || null;
    const stateName = (unaData as any)?.state || 'California';
    const headerLine = `${esc(unaName)}, a ${esc(stateName)} unincorporated nonprofit association, CA Filing No. ${fallback(filingNo)}, EIN ${fallback(ein)}, principal address ${fallback(principalAddress)}.`;

    // Attendees flowing sentences with mode + capacity, absent members noted.
    const attendees: any[] = meetingData?.actual_attendees || [];
    const absent: any[] = meetingData?.absent_members || [];
    const attendeeSentences = attendees.map((a: any) => {
      const name = esc(a.member_name || a.name || a.full_name || 'Unknown Member');
      const capacity = esc((a.member_role || a.capacity || 'Member').toString().replace(/_/g, ' '));
      const mode = esc((a.attendance_mode || a.mode || 'in person').toString());
      return `<p>Present ${mode}: ${name}, ${capacity}.</p>`;
    }).join('\n');
    const absentSentence = absent.length > 0
      ? `<p>Absent: ${absent.map((a: any) => esc(a.member_name || a.name || a.full_name || 'Unknown')).join(', ')}.</p>`
      : '';

    // Quorum statement.
    let quorumSentence = '';
    if (meetingData?.quorum_met === true) {
      quorumSentence = `<p>Quorum was present.</p>`;
    } else if (meetingData?.quorum_met === false) {
      quorumSentence = `<p>Quorum was not met.</p>`;
    }

    // Observances (Part B).
    const obs: any = meetingData?.observances || {};
    const observanceLines: string[] = [];
    if (obs.invocation_given) observanceLines.push(`<p>Invocation given.</p>`);
    const priorApproval = obs.prior_minutes_approval || {};
    if (priorApproval.prior_date) {
      const forVotes = Number(priorApproval.votes_for || 0);
      const againstVotes = Number(priorApproval.votes_against || 0);
      observanceLines.push(`<p>Minutes of ${formatDate(priorApproval.prior_date)} approved ${forVotes} to ${againstVotes}.</p>`);
    }
    if (obs.financial_report_delivered) {
      const office = esc(obs.financial_report_office || primaryRole);
      observanceLines.push(`<p>Financial report delivered by the ${office}.</p>`);
    }
    if (obs.stewardship_assessment_recorded) observanceLines.push(`<p>Stewardship assessment recorded.</p>`);

    // Agenda items and decisions with numeric votes.
    const agendaLines = (meetingData?.agenda_items || []).map((item: any) => {
      const title = esc(item.title || item.item || 'Agenda item');
      const vf = Number(item.votes_for ?? item.for ?? 0);
      const va = Number(item.votes_against ?? item.against ?? 0);
      if (item.decision || item.decided) {
        return `<p>${title}: approved ${vf} to ${va}.</p>`;
      }
      return `<p>${title}.</p>`;
    }).join('\n');

    // Motions with numeric votes.
    const motionLines = (meetingData?.motions || []).map((m: any) => {
      const text = esc(m.motion_text || m.description || 'Motion');
      const madeBy = esc(m.made_by || m.moved_by || '');
      const seconded = esc(m.seconded_by || '');
      const vf = Number(m.votes_for ?? 0);
      const va = Number(m.votes_against ?? 0);
      const outcome = (m.vote_result || m.status || '').toString();
      const outcomeLabel = outcome === 'approved' || outcome === 'adopted' || outcome === 'passed'
        ? `approved ${vf} to ${va}`
        : outcome
          ? `${esc(outcome)} ${vf} to ${va}`
          : `${vf} to ${va}`;
      const sourced = madeBy ? ` (moved by ${madeBy}${seconded ? `, seconded by ${seconded}` : ''})` : '';
      return `<p>${text}${sourced}: ${outcomeLabel}.</p>`;
    }).join('\n');

    // Decisions (if the app stores them separately).
    const decisionLines = (meetingData?.decisions || []).map((d: any) => {
      const text = esc(d.description || d.title || 'Decision');
      const vf = Number(d.votes_for ?? 0);
      const va = Number(d.votes_against ?? 0);
      return `<p>${text}: approved ${vf} to ${va}.</p>`;
    }).join('\n');

    // Resolutions adopted at the meeting, by number (Part C).
    const resolutionsAdoptedLines = (meetingData?.resolutions_noted || [])
      .filter((r: any) => (r.vote_result || '').toString() === 'adopted')
      .map((r: any) => {
        const num = r.resolution_number ? `Resolution ${r.resolution_number}` : `Resolution (${esc(r.title || 'Adopted')})`;
        const vf = Number(r.votes_for ?? 0);
        const va = Number(r.votes_against ?? 0);
        return `<p>${num} adopted ${vf} to ${va}.</p>`;
      }).join('\n');

    // Directed recordings pulled from general notes if present.
    const directedRecording = meetingData?.general_notes && String(meetingData.general_notes).trim().length > 0
      ? `<p>${esc(meetingData.general_notes)}</p>`
      : '';

    // Closing observances.
    const doctrineLine = obs.doctrine_reaffirmed ? `<p>Doctrine reaffirmed.</p>` : '';
    const benedictionLine = obs.benediction_given ? `<p>Benediction given.</p>` : '';

    // Board member signature blocks (unchanged marker contract).
    let boardSignatureBlocks = '';
    if (unaData?.una_members) {
      const boardMembers = unaData.una_members.filter((m: any) => {
        if (m.status !== 'active') return false;
        const role = (m.role || m.member_role || '').toLowerCase().trim().replace(/\s+/g, '_');
        return role.includes('board') || role.includes('member') || role === 'board_member' || role === 'board_member_active';
      });
      boardSignatureBlocks = boardMembers.map((member: any) => {
        const memberRole = member.role || member.member_role || 'Board Member';
        const formattedRole = memberRole.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        return `
    <div class="signature-block">
      <p><strong>Board Member Approval:</strong></p>
      <div class="signature-line" data-signer-id="${member.id}" data-signature-role="board_member"></div>
      <div class="signature-label">${esc(member.full_name)}, ${esc(formattedRole)}</div>
      <div style="margin-top: 0.15in;">Date: ____________________</div>
    </div>`;
      }).join('\n');
    }

    // Styles kept intact.
    const ecclesiasticalStyles = `
      @page { size: Letter; margin: 0.75in; @bottom-center { content: "Page " counter(page) " of " counter(pages); font-family: Cambria, Georgia, 'Times New Roman', serif; font-size: 9pt; color: #666; padding-bottom: 0.25in; } }
      body { font-family: Cambria, Georgia, 'Times New Roman', serif; font-size: 11pt; line-height: 1.85; color: #1a1a1a; background: white; margin: 0; padding: 0; }
      h1 { font-size: 16pt; font-weight: bold; margin: 0 0 14pt 0; text-align: center; text-transform: uppercase; letter-spacing: 0.10em; }
      .assoc-header { text-align: center; font-size: 10.5pt; color: #333; border-bottom: 2px solid #c9a961; padding-bottom: 14pt; margin-bottom: 20pt; }
      p { margin: 0 0 10pt 0; font-size: 11pt; }
      .signature-section { margin-top: 40pt; padding-top: 24pt; border-top: 1px solid #ccc; }
      .signature-block { margin-top: 30pt; margin-bottom: 25pt; padding: 20pt; border: 1px solid #ddd; border-radius: 4px; background-color: #fafafa; page-break-inside: avoid; }
      .signature-line { border-bottom: 2px solid #333; min-height: 0.6in; width: 3.5in; margin-bottom: 8pt; background-color: white; padding: 4pt; }
      .signature-label { font-size: 10pt; margin-top: 6pt; font-weight: 600; color: #333; }
    `;

    // Location and time paragraphs, each rendered only when present.
    const dateLocationTimeParts: string[] = [];
    dateLocationTimeParts.push(`<p>Held on ${meetingDate}${meetingLocation ? ` at ${esc(meetingLocation)}` : ''}${meetingStartTime ? `, beginning at ${formatTime12Hour(meetingStartTime)}` : ''}${meetingEndTime ? ` and concluding at ${formatTime12Hour(meetingEndTime)}` : ''}.</p>`);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Minutes of the ${esc(meetingType)} of ${esc(unaName)}</title>
  <style>${ecclesiasticalStyles}</style>
</head>
<body class="ecclesiastical-document">
  <h1>Minutes of the ${esc(meetingType)} of ${esc(unaName)}</h1>
  <div class="assoc-header">${headerLine}</div>

  <div class="content">
    ${dateLocationTimeParts.join('\n')}
    ${attendeeSentences}
    ${absentSentence}
    ${quorumSentence}
    ${observanceLines.join('\n')}
    ${agendaLines}
    ${motionLines}
    ${decisionLines}
    ${resolutionsAdoptedLines}
    ${directedRecording}
    ${doctrineLine}
    ${benedictionLine}

    <p style="margin-top: 24pt;">Respectfully recorded,</p>

    <div class="signature-section">
      <div class="signature-block">
        <div class="signature-line" data-signer-id="${treasurySecretaryUserId}" data-signature-role="primary_officer"></div>
        <div class="signature-label">${esc(recordingSecretaryName)}, ${esc(primaryRole)}</div>
        <div style="margin-top: 10pt;">Date: ${meetingDate}</div>
      </div>
      ${boardSignatureBlocks}
    </div>
  </div>
</body>
</html>`;
  };


  const loadMeetingData = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from("meeting_minutes")
        .select(`
          *,
          una:unas(name),
          recording_secretary:una_members!recording_secretary_id(full_name),
          presiding_officer:una_members!presiding_officer_id(full_name),
          certified_by_member:una_members!certified_by(full_name)
        `)
        .eq("id", meetingId)
        .single();

      if (error) throw error;
      
      setMeetingData(data);

      // Check if document already generated
      const { data: doc, error: docError } = await supabase
        .from("generated_documents")
        .select("*")
        .eq("task_id", taskId)
        .eq("document_type", "formal_meeting_minutes")
        .order("generated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (docError) {
        console.error('❌ [LOAD] Error querying documents:', docError);
        console.error('❌ [LOAD] Query details:', { taskId, document_type: 'formal_meeting_minutes' });
      }

      if (doc) {
        console.log('✅ [LOAD] Found existing document:', {
          id: doc.id,
          document_type: doc.document_type,
          status: doc.status,
          generated_at: doc.generated_at || 'NO TIMESTAMP'
        });
        setGeneratedDocument(doc);
      } else {
        console.warn('⚠️ [LOAD] No document found with query:', {
          taskId,
          document_type: 'formal_meeting_minutes'
        });
      }
    } catch (error) {
      console.error("Error loading meeting data:", error);
      toast({
        title: "Error",
        description: "Could not load meeting data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle data changes in edit mode - saves to database
  const handleDataChange = useCallback(async (updates: Partial<typeof meetingData>) => {
    // Update local state immediately for responsive UI
    setMeetingData((prev: any) => ({ ...prev, ...updates }));
    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from('meeting_minutes')
        .update(updates)
        .eq('id', meetingId);
        
      if (error) {
        console.error('Error saving meeting data:', error);
        toast({
          title: "Save failed",
          description: "Could not save changes. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error in handleDataChange:', error);
      toast({
        title: "Save failed",
        description: "Could not save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [meetingId, toast]);

  const handleGenerateDocument = async () => {
    try {
      setGenerating(true);

      // Generate HTML dynamically (matching ResolutionPhase1 pattern)
      const htmlContent = finishLegacyHtml(generateMeetingMinutesHtml(), {
        entityName: unaData?.name,
        sealEnabled: seal.sealEnabled,
        sealSvg: seal.sealSvg,
      });
      
      if (!htmlContent) {
        throw new Error('Failed to generate document content');
      }

      // Call edge function to generate PDF
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: htmlContent, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'PDF generation failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Response wasn't valid JSON
        }
        throw new Error(errorMessage);
      }

      const { pdf: pdfBase64, size } = await response.json();

      // Convert base64 to Blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      // Upload to storage
      const filePath = generatedFilePath(unaData?.name, 'formal_meeting_minutes', taskId);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = authSession?.user?.id || null;

      // Insert document record with custom_content (HTML for signature embedding)
      const documentData = {
        task_id: taskId,
        una_id: unaId,
        document_name: 'Formal Board Meeting Minutes',
        document_type: 'formal_meeting_minutes',
        file_path: filePath,
        file_url: filePath,
        file_size: pdfBlob.size,
        status: 'draft',
        custom_content: htmlContent,  // Critical: Store HTML for signature embedding
        created_by: createdBy,
        generated_at: new Date().toISOString(),
        merge_field_data: {
          template_key: 'formal_meeting_minutes',
          ...legacySigningMeta('formal_meeting_minutes', seal.sealEnabled),
          meeting_id: meetingId,
          una_id: unaId,
        },
      };

      // Check if document already exists for this task
      const { data: existingDoc } = await supabase
        .from('generated_documents')
        .select('id')
        .eq('task_id', taskId)
        .eq('document_type', 'formal_meeting_minutes')
        .maybeSingle();

      let doc;
      if (existingDoc) {
        // Update existing document
        const { data: updatedDoc, error: updateError } = await supabase
          .from('generated_documents')
          .update(documentData)
          .eq('id', existingDoc.id)
          .select()
          .single();

        if (updateError) throw updateError;
        doc = updatedDoc;
      } else {
        // Insert new document
        const { data: newDoc, error: insertError } = await supabase
          .from('generated_documents')
          .insert(documentData)
          .select()
          .single();

        if (insertError) throw insertError;
        doc = newDoc;
      }

      toast({
        title: "Document generated",
        description: "Meeting minutes document has been created successfully",
      });

      setGeneratedDocument(doc);
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error generating document:", error);
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Could not generate meeting minutes document",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSignaturesComplete = async (signatureResult: {
    treasury_secretary_signature?: string;
    treasury_secretary_signature_date?: string;
  }) => {
    if (!generatedDocument || !signatureResult.treasury_secretary_signature) {
      toast({
        title: "Error",
        description: "Missing document or signature data",
        variant: "destructive",
      });
      return;
    }

    // Note: member_id may be empty if Secretary Treasurer isn't in una_members table
    // This is acceptable as certified_by is nullable in meeting_minutes

    // Validate custom_content exists for signature embedding
    if (!generatedDocument.custom_content) {
      toast({
        title: "Document needs to be built again",
        description: "Build this document again so your signature can be added to it.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setCertifying(true);
      
      const treasurySecretaryUserId = treasurySecretaryData.user_id;
      const treasurySecretaryMemberId = treasurySecretaryData.member_id;
      const signedAt = signatureResult.treasury_secretary_signature_date 
        ? new Date(signatureResult.treasury_secretary_signature_date).toISOString()
        : new Date().toISOString();
      
      // Build signature data for embedding
      const signatureData = [{
        role: 'treasury_secretary',
        signer_id: treasurySecretaryUserId,
        signature_data: signatureResult.treasury_secretary_signature,
        signed_at: signedAt
      }];
      
      // Get the document's custom_content HTML
      const documentHtml = generatedDocument.custom_content;
      
      // Embed signature into HTML
      const updatedHtml = await embedSignaturesIntoHtml(
        documentHtml,
        signatureData,
        new Date().toISOString()
      );
      
      // Generate new PDF with embedded signature
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: updatedHtml, author: unaData?.name || undefined }),
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`PDF generation failed: ${errorText}`);
      }
      
      const { pdf: pdfBase64 } = await response.json();
      
      // Convert base64 to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Upload new PDF
      const signedFileName = generatedFilePath(unaData?.name, 'formal_meeting_minutes_signed', generatedDocument?.id || taskId);
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(signedFileName, pdfBlob, { contentType: 'application/pdf', upsert: true });
      
      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      // Buckets are private — store path as reference
      
      // Update document record with signature data
      const { error: updateDocError } = await supabase
        .from('generated_documents')
        .update({
          status: 'finalized',
          finalized_at: new Date().toISOString(),
          finalized_by: treasurySecretaryUserId,  // auth.users.id FK
          file_url: signedFileName,
          file_path: signedFileName,
          file_size: pdfBlob.size,
          signed_file_url: signedFileName,
          custom_content: updatedHtml,
          signature_history: {
            finalized_signatures: {
              treasury_secretary_signature: signatureResult.treasury_secretary_signature,
              treasury_secretary_date: signatureResult.treasury_secretary_signature_date,
              treasury_secretary_user_id: treasurySecretaryUserId,
              finalized_at: new Date().toISOString(),
            }
          }
        })
        .eq('id', generatedDocument.id);
      
      if (updateDocError) {
        throw new Error(`Document update failed: ${updateDocError.message}`);
      }
      
      // Update meeting_minutes record
      const meetingUpdateData: Record<string, any> = {
        status: 'finalized',
        certified_at: new Date().toISOString(),
      };
      
      // Only set certified_by if we have a valid member_id (Secretary Treasurer may not be in una_members)
      if (treasurySecretaryMemberId) {
        meetingUpdateData.certified_by = treasurySecretaryMemberId;
      }
      
      const { error: updateMinutesError } = await supabase
        .from('meeting_minutes')
        .update(meetingUpdateData)
        .eq('id', meetingId);
      
      if (updateMinutesError) {
        throw new Error(`Meeting minutes update failed: ${updateMinutesError.message}`);
      }
      
      setSignatureWizardOpen(false);
      await loadMeetingData();
      
      toast({
        title: "Minutes Certified Successfully! ✓",
        description: "The document is signed and closed. You can now ask your board members to sign.",
      });
      
      console.log('🔵 MeetingMinutesReview: Secretary Treasurer signed - waiting for member signature requests before completing phase');
    } catch (error) {
      console.error("Error finalizing with signatures:", error);
      toast({
        title: "Certification failed",
        description: error instanceof Error ? error.message : "Could not finalize meeting minutes",
        variant: "destructive",
      });
    } finally {
      setCertifying(false);
    }
  };

  const canCertify = () => {
    return generatedDocument && (
      meetingData?.status === "draft" || 
      meetingData?.status === "reviewing"
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!meetingData) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Meeting data not found. Please ensure the meeting has been recorded.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold">Review & Finalize Minutes</h2>
            <p className="text-muted-foreground">
              Review the meeting details and generate official minutes document
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Edit Mode Toggle - only show if not finalized */}
            {meetingData.status !== "finalized" && (
              <div className="flex items-center gap-2">
                {isSaving && (
                  <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Saving...
                  </Badge>
                )}
                <Label htmlFor="edit-mode" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
                  {editMode ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {editMode ? "Edit Mode" : "Review Mode"}
                </Label>
                <Switch
                  id="edit-mode"
                  checked={editMode}
                  onCheckedChange={setEditMode}
                />
              </div>
            )}

            <Badge variant={
              meetingData.status === "finalized" ? "default" :
              meetingData.status === "draft" ? "secondary" :
              "outline"
            }>
              {meetingData.status === "finalized" ? "Finalized ✓" :
               meetingData.status === "draft" ? "Draft" :
               "Recording"}
            </Badge>
          </div>
        </div>

        {/* Meeting Info Summary */}
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Meeting Type</p>
              <p className="font-semibold">{meetingData.meeting_type}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Date</p>
              <p className="font-semibold">
                {new Date(meetingData.meeting_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Duration</p>
              <p className="font-semibold">
                {formatTime12Hour(meetingData.meeting_start_time)} - {meetingData.meeting_end_time ? formatTime12Hour(meetingData.meeting_end_time) : "Ongoing"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Attendees</p>
              <p className="font-semibold">
                {meetingData.actual_attendees.length} present
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Separator />

      {/* Review Tabs */}
      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="summary">
            <FileText className="h-4 w-4 mr-2" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="attendees">
            <Users className="h-4 w-4 mr-2" />
            Attendees
          </TabsTrigger>
          <TabsTrigger value="agenda">
            <ClipboardList className="h-4 w-4 mr-2" />
            Agenda
          </TabsTrigger>
          <TabsTrigger value="motions">
            <Gavel className="h-4 w-4 mr-2" />
            Motions
          </TabsTrigger>
          <TabsTrigger value="actions">
            <CheckSquare className="h-4 w-4 mr-2" />
            Actions
          </TabsTrigger>
          <TabsTrigger value="resolutions">
            <Shield className="h-4 w-4 mr-2" />
            Resolutions
          </TabsTrigger>
          <TabsTrigger value="transcript">
            <FileText className="h-4 w-4 mr-2" />
            Transcript
          </TabsTrigger>
        </TabsList>

        {/* Edit Mode Warning */}
        {editMode && (
          <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <Pencil className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Edit Mode Active:</strong> Changes are saved automatically. If you've already generated a document, you'll need to regenerate it after making edits.
            </AlertDescription>
          </Alert>
        )}

        <TabsContent value="summary">
          <ReviewSummaryTab data={meetingData} />
        </TabsContent>

        <TabsContent value="attendees">
          {editMode ? (
            <AttendeesSection
              data={{
                expected_attendees: meetingData.expected_attendees || [],
                actual_attendees: meetingData.actual_attendees || [],
                special_guests: meetingData.special_guests || [],
                absent_members: meetingData.absent_members || [],
                quorum_required: meetingData.quorum_required ?? false,
                quorum_threshold: meetingData.quorum_threshold,
              }}
              onChange={handleDataChange}
              unaId={unaId}
            />
          ) : (
            <ReviewAttendeesTab data={meetingData} />
          )}
        </TabsContent>

        <TabsContent value="agenda">
          {editMode ? (
            <AgendaSection
              data={{ agenda_items: meetingData.agenda_items || [] }}
              onChange={handleDataChange}
            />
          ) : (
            <ReviewAgendaTab data={meetingData} />
          )}
        </TabsContent>

        <TabsContent value="motions">
          {editMode ? (
            <MotionsSection
              data={{
                motions: meetingData.motions || [],
                decisions: meetingData.decisions || [],
              }}
              onChange={handleDataChange}
            />
          ) : (
            <ReviewMotionsTab data={meetingData} />
          )}
        </TabsContent>

        <TabsContent value="actions">
          {editMode ? (
            <ActionItemsSection
              data={{ action_items: meetingData.action_items || [] }}
              onChange={handleDataChange}
              unaId={unaId}
            />
          ) : (
            <ReviewActionsTab data={meetingData} />
          )}
        </TabsContent>

        <TabsContent value="resolutions">
          {editMode ? (
            <ResolutionsSection
              data={{
                resolutions_noted: meetingData.resolutions_noted || [],
                actual_attendees: meetingData.actual_attendees || [],
              }}
              onChange={handleDataChange}
              unaId={unaId}
            />
          ) : (
            <ReviewResolutionsTab data={meetingData} />
          )}
        </TabsContent>

        <TabsContent value="transcript">
          {editMode ? (
            <NotesSection
              data={{
                general_notes: meetingData.general_notes || '',
                has_transcript: meetingData.has_transcript ?? false,
                transcript_text: meetingData.transcript_text || '',
                transcript_file_url: meetingData.transcript_file_url || '',
              }}
              onChange={handleDataChange}
            />
          ) : (
            <ReviewTranscriptTab data={meetingData} />
          )}
        </TabsContent>
      </Tabs>

      <Separator />

      {/* Action Buttons */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Document Actions</h3>
            <p className="text-sm text-muted-foreground">
              Generate the official minutes document, sign it as {unaData?.primary_officer_role || 'Secretary Treasurer'}, then request board member signatures
            </p>
          </div>

          <SealApplyCheckbox
            available={seal.sealAvailable}
            checked={seal.applySeal}
            onChange={seal.setApplySeal}
          />
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleGenerateDocument}
              disabled={generating || meetingData.status === "finalized"}
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4 mr-2" />
                  {generatedDocument ? "Regenerate Document" : "Generate Minutes Document"}
                </>
              )}
            </Button>

            {generatedDocument && (
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                size="lg"
              >
                <FileText className="h-4 w-4 mr-2" />
                Preview Document
              </Button>
            )}

            {/* Sign & Finalize button - only show if not yet finalized */}
            {generatedDocument?.status !== 'finalized' && (
              <Button
                onClick={() => setSignatureWizardOpen(true)}
                disabled={!canCertify() || certifying}
                size="lg"
                variant="default"
              >
                {certifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Sign & Finalize
                  </>
                )}
              </Button>
            )}

            {/* Request Member Signatures button - show after finalization */}
            {generatedDocument?.status === 'finalized' && (
              <div className="w-full">
                <DocumentSigningControl
                  doc={generatedDocument}
                  unaId={unaId}
                  sealEnabled={seal.sealAvailable}
                />
              </div>
            )}
          </div>

          {!generatedDocument && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Generate the minutes document before signing
              </AlertDescription>
            </Alert>
          )}

          {generatedDocument && generatedDocument.status !== 'finalized' && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Click "Sign &amp; Finalize" to capture your signature as {unaData?.primary_officer_role || 'Secretary Treasurer'}. After finalization, you can request board member signatures.
              </AlertDescription>
            </Alert>
          )}

          {meetingData.status === "finalized" && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                These minutes have been certified and finalized on{" "}
                {new Date(meetingData.certified_at).toLocaleDateString()} by{" "}
                {meetingData.certified_by_member?.full_name || "the certifying officer"}.
                You can now request board member signatures.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </Card>

      {/* Phase Completion Section - Always visible */}
      <Card className="p-6">
        <Alert className={generatedDocument?.status !== 'finalized' ? 'border-muted bg-muted/20' : 'border-green-500/50 bg-green-50 dark:bg-green-950/20'}>
          <AlertDescription>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">
                  {generatedDocument?.status === 'finalized' ? 'Minutes Signed' : 'Complete Phase 3'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {generatedDocument?.status === 'finalized'
                    ? `Document signed by ${unaData?.primary_officer_role || 'Secretary Treasurer'}. Ready to proceed to distribution.`
                    : 'Sign and finalize the document to continue to Phase 4.'}
                </p>
              </div>
              <Button 
                onClick={() => {
                  if (generatedDocument?.status !== 'finalized') {
                    toast({
                      title: 'Signature Required',
                      description: 'Sign and close the document first, then you can finish this phase.',
                      variant: 'destructive',
                    });
                    return;
                  }
                  setIsCompleting(true);
                  toast({
                    title: 'Phase 3 Complete',
                    description: 'Minutes are closed. Moving on to Phase 4: share and file.',
                  });
                  onPhaseComplete?.();
                  setIsCompleting(false);
                }}
                disabled={generatedDocument?.status !== 'finalized' || isCompleting}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Complete Phase 3'
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </Card>

      {/* Document Preview Modal */}
      {generatedDocument && previewOpen && (
        <DocumentPreviewModal
          document={generatedDocument}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          onSignAndFinalize={() => {
            setPreviewOpen(false);
            setSignatureWizardOpen(true);
          }}
        />
      )}

      {/* Finalize With Signatures Modal */}
      {signatureWizardOpen && (
        <FinalizeWithSignatures
          documentTitle={`Sign & Finalize Meeting Minutes`}
          treasurySecretaryName={treasurySecretaryData?.full_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          requiredSigners={['treasury_secretary']}
          onFinalize={handleSignaturesComplete}
          onCancel={() => setSignatureWizardOpen(false)}
        />
      )}
    </div>
  );
}
